import { useEffect, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Field, FieldProps } from 'formik';
import { EntityOption, ENTITY_FETCHERS, ENTITY_GET_BY_ID } from './entity-fetchers';

interface Props {
    field: string;
    value: string | null;
    onChange: (id: string | null) => void;
    label: string;
    error?: string;
    required?: boolean;
    helperText?: string;
    disabled?: boolean;
    sx?: object;
    /** Only offer entities from this workspace (supported by the rule_group fetcher). */
    workspaceId?: string;
}

/**
 * Single-select entity picker for forms. Searches server-side as you type, so it does
 * not depend on the selection being inside the first page — and resolves the current
 * value by id when it is not, which is how an offer whose network is older than the
 * page rendered blank while still holding the id.
 */
export function EntitySelectField({ field, value, onChange, label, error, required, helperText, disabled, sx, workspaceId }: Props) {
    const fetcher = ENTITY_FETCHERS[field];
    const getById = ENTITY_GET_BY_ID[field];
    const [input, setInput] = useState('');
    const [search, setSearch] = useState('');

    useEffect(() => {
        const t = setTimeout(() => setSearch(input.trim()), 250);
        return () => clearTimeout(t);
    }, [input]);

    const { data: options = [], isFetching } = useQuery({
        queryKey: ['entity-select', field, search, workspaceId],
        queryFn: () => fetcher({ search: search || undefined, limit: 20, workspaceIds: workspaceId ? [workspaceId] : undefined })
            .then(r => r.data.map(x => ({ id: x.id, name: x.name }))),
        enabled: !!fetcher,
    });

    const { data: current } = useQuery({
        queryKey: ['entity-select-by-id', field, value],
        queryFn: () => getById(value as string).then(x => ({ id: x.id, name: x.name })),
        enabled: !!value && !!getById && !options.some(o => o.id === value),
    });

    const all: EntityOption[] = current && !options.some(o => o.id === current.id)
        ? [current, ...options] : options;
    const selected = all.find(o => o.id === value) || null;

    return (
        <Autocomplete
            size="small"
            options={all}
            value={selected}
            loading={isFetching}
            disabled={disabled}
            getOptionLabel={(o: EntityOption) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            filterOptions={(x) => x}
            onInputChange={(_e, v, reason) => { if (reason !== 'reset') setInput(v); }}
            onChange={(_e, v) => onChange(v?.id || null)}
            sx={sx}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    size="small"
                    required={required}
                    error={!!error}
                    helperText={error || helperText || ' '}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isFetching ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
}

/** Formik binding, so touched/error and validation come from the schema. */
export function EntitySelectFieldFormik({ name, onChanged, ...props }: Omit<Props, 'value' | 'onChange' | 'error'> & { name: string; onChanged?: (id: string | null) => void }) {
    return (
        <Field name={name}>
            {({ field: f, meta, form }: FieldProps) => (
                <EntitySelectField
                    {...props}
                    value={f.value || null}
                    error={meta.touched && meta.error ? meta.error : undefined}
                    onChange={(id) => {
                        form.setFieldValue(name, id);
                        form.setFieldTouched(name, true, false);
                        onChanged?.(id);
                    }}
                />
            )}
        </Field>
    );
}
